import { Component, OnInit, SecurityContext, ViewEncapsulation, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { InputMaskModule } from '@ngneat/input-mask';
import { debounceTime, distinctUntilChanged, filter, Observable, startWith, tap } from 'rxjs';
import { AllocatedPriceSign, PriceSignLocation, UnallocatedPriceSign } from '../price-sign.model';
import { PriceSignService } from 'src/app/shared/services/price-sign.service';
import { DomSanitizer } from '@angular/platform-browser';
import { SnackbarService, FeedbackSuccessComponent } from 'ok-angular-shared/components';
import { HttpErrorResponse } from '@angular/common/http';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { AsyncPipe } from '@angular/common';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatHint, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/core';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'adm-price-sign-allocation-modal',
  templateUrl: './price-sign-allocation-modal.component.html',
  styleUrl: './price-sign-allocation-modal.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [
    InputMaskModule,
    CustomDropDownArrowDirective,
    ReactiveFormsModule,
    AsyncPipe,
    FeedbackSuccessComponent,
    MatDialogTitle,
    MatDialogContent,
    MatIconButton,
    MatDialogActions,
    MatButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatHint,
    MatSuffix,
    MatInput,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
    MatTooltip,
  ],
})
export class PriceSignAllocationModalComponent implements OnInit {
  dialogRef = inject<MatDialogRef<PriceSignAllocationModalComponent>>(MatDialogRef);
  data = inject<{
    unallocatedPriceSigns$: Observable<UnallocatedPriceSign[]>;
    priceSign: AllocatedPriceSign;
  }>(MAT_DIALOG_DATA);
  private priseSignService = inject(PriceSignService);
  private sanitizer = inject(DomSanitizer);
  private snackbarService = inject(SnackbarService);

  public unallocatedPriceSigns$: Observable<UnallocatedPriceSign[]> = new Observable<UnallocatedPriceSign[]>();
  public allocatedPriceSign: AllocatedPriceSign | null = null;
  public locations$: Observable<PriceSignLocation[]> = new Observable<PriceSignLocation[]>();
  public typeAheadLocationModel: FormGroup = new FormGroup({});
  public filteredLocations: PriceSignLocation[] = [];
  public allocatedLocation: PriceSignLocation | null = null;
  async ngOnInit(): Promise<void> {
    this.unallocatedPriceSigns$ = this.data.unallocatedPriceSigns$;
    this.allocatedPriceSign = this.data.priceSign;

    this.typeAheadLocationModel = this.initLocationTypeAhead();
  }

  public initLocationTypeAhead() {
    const typeAheadFormGroup = new FormGroup({
      typeAheadQuery: new FormControl(this.allocatedPriceSign?.charging_station_location_name || ''),
    });

    typeAheadFormGroup.valueChanges
      .pipe(
        filter(val => (val.typeAheadQuery?.length ?? 0) >= 2),
        startWith(''), // let one blank through to search for all
        debounceTime(300),
        distinctUntilChanged(),
        tap(searchTerm => this.sanitizer.sanitize(SecurityContext.URL, searchTerm))
      )
      .subscribe((val: any) => {
        this.priseSignService.getPriceSignLocations(val.typeAheadQuery).subscribe(locations => {
          this.filteredLocations = locations;
        });
      });
    return typeAheadFormGroup;
  }

  public createLocation(priceSign: UnallocatedPriceSign, location: PriceSignLocation) {
    this.priseSignService.allocatePriceSign(priceSign.price_sign_identifier, location.charging_station_location_identifier).subscribe({
      next: (relationIdentifier: string) => {
        this.snackbarService.success('Prisskiltet blev succesfuldt tilknyttet en lokation.');
        priceSign.relation_identifier = relationIdentifier;
        priceSign.allocated_charging_station_location = location;
        this.initLocationTypeAhead();
      },
      error: (err: HttpErrorResponse) => {
        this.snackbarService.error(`Der skete fejl under oprettelse: (${err.error.code})`);
      },
    });
  }

  public editLocation(priceSign: AllocatedPriceSign, location: PriceSignLocation) {
    this.priseSignService.editAllocatePriceSign(priceSign.price_sign_identifier, location.charging_station_location_identifier).subscribe({
      next: () => {
        this.snackbarService.success('Prisskilt tilknytning blev succesfuldt opdateret.');
      },
      error: (err: HttpErrorResponse) => {
        this.snackbarService.error(`Der skete fejl under redigering: (${err.error.code})`);
      },
    });
  }

  public removeLocation(priceSign: UnallocatedPriceSign | AllocatedPriceSign) {
    if (!priceSign.relation_identifier) {
      return;
    }

    this.priseSignService.unallocatePriceSign(priceSign.relation_identifier).subscribe({
      next: () => {
        this.snackbarService.success('Tilknytning til lokation er fjernet fra Prisskiltet.');
        if (!this.allocatedPriceSign) {
          priceSign.relation_identifier = null;
          priceSign.allocated_charging_station_location = null;
          this.typeAheadLocationModel.get('typeAheadQuery')?.setValue('');
        } else {
          this.close();
        }
      },
      error: (err: HttpErrorResponse) => {
        this.snackbarService.error(`Der skete fejl under sletning: (${err.error.code})`);
      },
    });
  }

  public displayFn(chargingStationLocation: PriceSignLocation | string): string {
    if (chargingStationLocation && typeof chargingStationLocation !== 'string') {
      return `${chargingStationLocation.charging_station_location_name}, ${chargingStationLocation.organization_name}`;
    } else {
      return (chargingStationLocation as string) || '';
    }
  }

  public close() {
    this.dialogRef.close(this.allocatedPriceSign);
  }
}
